import { useState, useEffect, useRef, useCallback } from 'react';
import { useWindowSize } from '../hooks/useWindowSize';
import { authService } from '../utils/auth.ts';
import { RotateLeft, RotateRight, Save, Close } from '@mui/icons-material';

interface Photo {
  PK: string;
  filename: string;
  s3_key: string;
  uploader_id: string;
  uploader_username: string;
  uploaded_at: string;
  file_size: number;
}

interface ThumbnailEditModalProps {
  photo: Photo;
  onClose: () => void;
  onThumbnailUpdated: () => void;
}

const ThumbnailEditModal: React.FC<ThumbnailEditModalProps> = ({
  photo,
  onClose,
  onThumbnailUpdated,
}) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [rotation, setRotation] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const originalImageRef = useRef<HTMLImageElement | null>(null);

  // PKからphotoIDを抽出する関数
  const getPhotoIdFromPK = (pk: string): string => {
    // PK format: "TENANT#{tenantId}#PHOTO#{photoId}"
    const parts = pk.split('#');
    return parts.length >= 4 ? parts[3] : pk;
  };

  // 共通フックでモバイル判定
  const { isMobile } = useWindowSize();

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        setError('');

        // サムネイル画像URL取得
        const photoId = getPhotoIdFromPK(photo.PK);
        const response = await authService.authFetch(
          `/photos/${photoId}/thumbnail`
        );
        if (!response.ok) {
          throw new Error('サムネイル画像の取得に失敗しました');
        }
        const data = await response.json();
        setImageUrl(data.url);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'エラーが発生しました');
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [photo.PK]);

  // 画像を回転させてキャンバスに描画
  const drawRotatedImage = useCallback(() => {
    const canvas = canvasRef.current;
    const img = originalImageRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 回転角度をラジアンに変換
    const angleInRadians = (rotation * Math.PI) / 180;

    // 回転後のキャンバスサイズを計算
    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;

    let newWidth, newHeight;
    if (rotation % 180 === 0) {
      newWidth = imgWidth;
      newHeight = imgHeight;
    } else {
      newWidth = imgHeight;
      newHeight = imgWidth;
    }

    // キャンバスサイズを調整
    canvas.width = newWidth;
    canvas.height = newHeight;

    // キャンバスをクリア
    ctx.clearRect(0, 0, newWidth, newHeight);

    // 回転の中心点を設定
    ctx.translate(newWidth / 2, newHeight / 2);
    ctx.rotate(angleInRadians);

    // 画像を描画
    ctx.drawImage(img, -imgWidth / 2, -imgHeight / 2, imgWidth, imgHeight);

    // 変換をリセット
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }, [rotation]);

  // 画像が読み込まれたら描画
  const handleImageLoad = () => {
    drawRotatedImage();
  };

  // 回転が変更されたら再描画
  useEffect(() => {
    if (originalImageRef.current && originalImageRef.current.complete) {
      drawRotatedImage();
    }
  }, [drawRotatedImage, originalImageRef]);

  const handleRotateLeft = () => {
    setRotation(prev => (prev - 90 + 360) % 360);
  };

  const handleRotateRight = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // FormDataを作成（回転角度のみ）
      const formData = new FormData();
      formData.append('rotation', rotation.toString());

      // サムネイル更新API呼び出し
      const photoId = getPhotoIdFromPK(photo.PK);
      const response = await authService.authFetch(
        `/photos/${photoId}/thumbnail`,
        {
          method: 'PUT',
          body: formData,
          invalidateCache: `/photos/${photoId}/thumbnail`, // サムネイルキャッシュを無効化
        }
      );

      if (!response.ok) {
        throw new Error('サムネイルの更新に失敗しました');
      }

      onThumbnailUpdated();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : '保存中にエラーが発生しました'
      );
    } finally {
      setSaving(false);
    }
  };

  // ESCキーでモーダルを閉じる
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1002,
        padding: isMobile ? '0' : '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--neutral-50)',
          borderRadius: isMobile ? '12px 12px 0 0' : '12px',
          maxWidth: isMobile ? '100vw' : '80vw',
          maxHeight: isMobile ? '100vh' : '90vh',
          width: isMobile ? '100%' : 'auto',
          height: isMobile ? '100%' : 'auto',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          position: isMobile ? 'fixed' : 'relative',
          top: isMobile ? '0' : 'auto',
          left: isMobile ? '0' : 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div
          style={{
            padding: isMobile ? '12px 16px' : '20px',
            borderBottom: '1px solid var(--neutral-200)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2
            style={{
              margin: 0,
              color: 'var(--neutral-800)',
              fontSize: isMobile ? '1.1rem' : '1.5rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              marginRight: '12px',
            }}
          >
            サムネイル編集 - {photo.filename}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: isMobile ? '20px' : '24px',
              cursor: 'pointer',
              color: 'var(--neutral-600)',
              padding: '4px',
              minWidth: isMobile ? '32px' : 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Close />
          </button>
        </div>

        {/* メインコンテンツ */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: isMobile ? '16px' : '20px',
            overflow: 'auto',
          }}
        >
          {loading && (
            <div
              style={{
                textAlign: 'center',
                color: 'var(--neutral-600)',
                padding: '40px',
              }}
            >
              読み込み中...
            </div>
          )}

          {error && (
            <div
              style={{
                textAlign: 'center',
                color: 'var(--error)',
                padding: '20px',
                backgroundColor: 'var(--error-bg)',
                borderRadius: '8px',
                marginBottom: '20px',
              }}
            >
              {error}
            </div>
          )}

          {imageUrl && !loading && (
            <>
              {/* 画像プレビューエリア */}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'var(--neutral-100)',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  position: 'relative',
                  minHeight: isMobile ? '250px' : '400px',
                }}
              >
                {/* オリジナル画像（非表示、回転処理用） */}
                <img
                  ref={originalImageRef}
                  src={imageUrl}
                  alt={photo.filename}
                  style={{ display: 'none' }}
                  onLoad={handleImageLoad}
                  crossOrigin="anonymous"
                />

                {/* 回転されたプレビュー用キャンバス */}
                <canvas
                  ref={canvasRef}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    border: '2px solid var(--neutral-300)',
                    borderRadius: '4px',
                  }}
                />

                {/* 回転角度表示 */}
                {rotation !== 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                    }}
                  >
                    {rotation}°
                  </div>
                )}
              </div>

              {/* コントロールボタン */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <button
                  onClick={handleRotateLeft}
                  style={{
                    backgroundColor: 'var(--neutral-600)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: isMobile ? '12px 16px' : '10px 16px',
                    cursor: 'pointer',
                    fontSize: isMobile ? '14px' : '16px',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  title="左に90度回転"
                >
                  <RotateLeft />
                  左回転
                </button>

                <button
                  onClick={handleRotateRight}
                  style={{
                    backgroundColor: 'var(--neutral-600)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: isMobile ? '12px 16px' : '10px 16px',
                    cursor: 'pointer',
                    fontSize: isMobile ? '14px' : '16px',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  title="右に90度回転"
                >
                  <RotateRight />
                  右回転
                </button>

                <div style={{ flex: 1, minWidth: '20px' }} />

                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    backgroundColor: saving
                      ? 'var(--neutral-400)'
                      : 'var(--primary-600)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: isMobile ? '12px 20px' : '10px 20px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '500',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  title="変更を保存"
                >
                  <Save />
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>

              {/* 説明テキスト */}
              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: 'var(--primary-50)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: 'var(--neutral-700)',
                  lineHeight: '1.5',
                }}
              >
                <strong>📷 EXIF情報修正機能</strong>
                <br />
                画像を回転させることで、正しい向きでサムネイルが表示されるようになります。
                回転した画像は新しいサムネイルとして生成され、タイムラインに反映されます。
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThumbnailEditModal;
